import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: sticky summary layout,
 * the installments CTA close-on-click, locking background scroll while the pełnoekranowy
 * (full-screen) desktop cart is open, and pushing a `view_cart` analytics event.
 *
 * Note: automatic drawer opening on add-to-cart was removed 14 September 2026 (punkt 6,
 * Poprawki_Fondori.pdf) — a popup now offers the customer a choice instead
 * (see snippets/cart-added-popup.liquid). The `auto-open` attribute this component used to
 * read is no longer set on <cart-drawer-component> in snippets/cart-drawer.liquid.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    // Safety net in case the component is torn down (e.g. section re-render) while it
    // still owns a scroll lock — unlockScroll() on an owner that never locked is a no-op.
    unlockScroll(this);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state, wires up the installments CTA,
   * locks background scroll, and pushes a view_cart analytics event.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#pushViewCartEvent();

    // <theme-drawer> only locks scroll itself in modal mode (<990px, native showModal()).
    // In sidebar/squeeze mode (≥990px) it deliberately leaves the page scrollable, which is
    // correct for the narrow sidebar drawers (filter/menu/chat) but not for our pełnoekranowy
    // (full-screen, punkt 8 Poprawki_Fondori.pdf) cart, which should block background scroll
    // like any full-screen takeover. We add our own scroll-lock owner here — harmless and
    // redundant with <theme-drawer>'s own lock in modal mode (lockScroll owners are a Set),
    // and the only lock in sidebar mode. Uses the theme's own lockScroll/unlockScroll
    // (@theme/utilities) rather than a `body:has(#cart-drawer[open])` CSS rule — `:has()`
    // re-evaluation during the cart's empty-state View Transition (component-cart-items.js,
    // startViewTransition(..., ['empty-cart-drawer'])) was found to freeze the tab for
    // 20-30s when removing the last item (reported by Daniel 14 September 2026); utilities.js
    // itself notes it avoids `:has()` for exactly this performance reason.
    lockScroll(this);

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * Handles the theme-drawer closing — releases the scroll lock acquired in #handleDrawerOpen.
   */
  #handleDrawerClose = () => {
    unlockScroll(this);
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    event.promise
      ?.then(() => {
        requestAnimationFrame(() => this.#updateStickyState());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /**
   * Pushes a `view_cart` event to window.dataLayer every time the cart drawer opens —
   * whether opened via the "Przejdź do koszyka" popup button, the header cart icon, or
   * anything else. Placeholder for the future GA4/Google Ads setup (patrz otwarty punkt 23
   * w podsumowaniu projektu — analityka jeszcze nie zainstalowana na 14 września 2026).
   * Safe no-op today: the array just queues events until a tag manager reads it.
   */
  #pushViewCartEvent() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'view_cart' });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
